import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

// Customer-facing copy, mapped 1:1 to backend outcomes. Exported so the page
// (fetch/toast layer) and tests can assert the exact strings.
export const PLAYER_COPY = Object.freeze({
    // 410 video_unavailable
    expired:
        "This video has expired and is no longer available. Finished videos are stored for 45 days.",
    // 503 media_check_unavailable (also used for download service errors)
    service: "We couldn't load this video right now. Please try again.",
    // HTML5 media decode / load error
    media: "We couldn't play this video. Try loading it again.",
});

// The player surface lives INSIDE the Radix dialog content, so Radix mounts it
// only while open (guaranteeing zero <video> nodes when closed and exactly one
// while open). Its unmount cleanup runs on the LIVE element (React runs effect
// cleanups before detaching the node), satisfying the required close teardown:
// pause -> removeAttribute('src') -> load().
const PlayerSurface = ({ src, loading, fetchError, onRetryFetch }) => {
    const videoRef = useRef(null);
    const [mediaError, setMediaError] = useState(false);

    // Assign / refresh the source imperatively (mirrors the codebase's audio
    // pattern) so we can precisely removeAttribute('src') on teardown.
    useEffect(() => {
        const el = videoRef.current;
        if (el && src) {
            try {
                el.src = src;
                el.load();
            } catch (_e) {
                /* noop */
            }
        }
    }, [src]);

    // A newly minted source clears any prior decode error.
    useEffect(() => {
        setMediaError(false);
    }, [src]);

    // Unmount teardown — runs on close (Radix unmounts this subtree) and on any
    // parent unmount. The element is CAPTURED at mount time because React may
    // null the ref before this passive-effect cleanup runs; using the captured
    // node guarantees pause -> removeAttribute('src') -> load() hit a live node.
    useEffect(() => {
        const el = videoRef.current;
        return () => {
            if (el) {
                try {
                    el.pause();
                    el.removeAttribute("src");
                    el.load();
                } catch (_e) {
                    /* noop */
                }
            }
        };
    }, []);

    // A fetch error (410/503) takes precedence over a local media decode error.
    const shownError = fetchError || (mediaError ? PLAYER_COPY.media : null);
    const retry = fetchError
        ? onRetryFetch || null
        : mediaError
          ? () => {
                setMediaError(false);
                const el = videoRef.current;
                if (el && src) {
                    try {
                        el.load();
                    } catch (_e) {
                        /* noop */
                    }
                }
            }
          : null;

    return (
        <div className="relative bg-black">
            <video
                ref={videoRef}
                data-testid="video-player-element"
                controls
                playsInline
                preload="metadata"
                onError={() => {
                    const el = videoRef.current;
                    // Ignore the synthetic error fired when we clear the source.
                    if (el && el.getAttribute("src")) setMediaError(true);
                }}
                className="block max-h-[70vh] w-full bg-black"
            />
            {loading && !shownError && (
                <div
                    data-testid="video-player-loading"
                    className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60 text-white/70"
                >
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            )}
            {shownError && (
                <div
                    data-testid="video-player-error"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 p-6 text-center"
                >
                    <AlertTriangle className="h-7 w-7 text-amber-300" />
                    <p className="max-w-md text-sm text-white/85">{shownError}</p>
                    {retry && (
                        <button
                            type="button"
                            data-testid="video-player-retry"
                            onClick={retry}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/15 min-h-11 px-3 text-sm font-semibold text-white/85 transition-colors hover:border-cyan-300/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Try again
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Single shared focused player for the whole page. Accessibility (role=dialog,
// aria-modal, Escape-to-close, focus entry/return, Tab/Shift+Tab containment,
// labeled Close button) is provided by the existing Radix dialog primitive
// (@/components/ui/dialog) — no new dependency is introduced.
export const VideoPlayerDialog = ({
    open,
    title,
    src,
    loading,
    error,
    onClose,
    onRetry,
    returnFocusRef,
}) => {
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose && onClose();
            }}
        >
            <DialogContent
                data-testid="video-player-dialog"
                aria-modal="true"
                onCloseAutoFocus={(event) => {
                    // Radix only auto-restores focus to a DialogTrigger; our
                    // trigger is a card Play button, so restore focus to it here.
                    const el = returnFocusRef && returnFocusRef.current;
                    if (el && typeof el.focus === "function") {
                        event.preventDefault();
                        try {
                            el.focus();
                        } catch (_e) {
                            /* noop */
                        }
                    }
                }}
                className="w-[92vw] max-w-3xl gap-0 overflow-hidden border-white/12 bg-surface-raised p-0 sm:rounded-lg"
            >
                <DialogHeader className="border-b border-white/10 px-4 py-3 text-left">
                    <DialogTitle
                        data-testid="video-player-title"
                        className="truncate pr-8 text-base font-semibold text-white"
                    >
                        {title || "Video"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Video playback for {title || "your finished video"}.
                    </DialogDescription>
                </DialogHeader>
                <PlayerSurface
                    src={src}
                    loading={loading}
                    fetchError={error}
                    onRetryFetch={onRetry}
                />
            </DialogContent>
        </Dialog>
    );
};
